import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePracticeContent } from '../hooks/usePracticeContent';

const NAVY = '#002F6C';

// Earth palette. Triple = [highlight-ring, mid (fill+stroke), deep (label text)].
// key remap from the approved scene: g->l (landscape), y->i (interiors), r->a (architecture).
const EA = {
  l: ['#AEB98F', '#6E7D4A', '#3F4A28'], // landscape   — sage / olive
  i: ['#E0B896', '#B5713F', '#6E3F1F'], // interiors   — clay / terracotta
  a: ['#C9BFB0', '#8B7B65', '#4F4536'], // architecture — stone / umber
};

const CORE = { x: 230, y: 210, r: 74 };

// Ported scene positions. Labeled radii are overwritten from real counts (override 2);
// the small unlabeled satellites keep their given radii (decorative texture only).
const BASE = [
  { k: 'l', x: 118, y: 132, label: 'landscape' },
  { k: 'l', x: 95, y: 220, r: 30 },
  { k: 'l', x: 160, y: 70, r: 22 },
  { k: 'i', x: 338, y: 112, label: 'interiors' },
  { k: 'i', x: 375, y: 180, r: 18 },
  { k: 'a', x: 342, y: 302, label: 'architecture' },
  { k: 'a', x: 390, y: 260, r: 14 },
  { k: 'l', x: 150, y: 325, r: 26 },
  { k: 'i', x: 290, y: 65, r: 14 },
  { k: 'a', x: 300, y: 360, r: 12 },
];

const PracticeBubbles = () => {
  const { data } = usePracticeContent();
  const d = (data && data.disciplines) || {};
  // seed fallback so the scene never breaks on fetch failure
  const landscape = Number(d.landscape_count) || 575;
  const interiors = Number(d.interiors_count) || 300;
  const architecture = Number(d.architecture_count) || 125;

  // Size ∝ real counts: r = 62 * sqrt(count / 575) for the three LABELED bubbles.
  const sats = useMemo(() => {
    const rFor = (c) => 62 * Math.sqrt(c / 575);
    const labeledR = { landscape: rFor(landscape), interiors: rFor(interiors), architecture: rFor(architecture) };
    return BASE.map((b) => ({ ...b, r: b.label ? labeledR[b.label] : b.r }));
  }, [landscape, interiors, architecture]);

  // One-shot staggered entrance. Initialise `true` (final state) on reduced-motion
  // / coarse pointer so the static fallback shows immediately with no transition.
  const [entered, setEntered] = useState(() =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    window.matchMedia('(pointer: coarse)').matches
  );
  const ENTER_DELAY = { landscape: 0, interiors: 140, architecture: 280 };

  const svgRef = useRef(null);
  const coreRef = useRef(null);
  const groupRefs = useRef([]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const coarse = window.matchMedia('(pointer: coarse)').matches;

    // static render on reduced-motion / mobile — no rAF, no repulsion
    if (reduce || coarse) {
      sats.forEach((b, i) => {
        const g = groupRefs.current[i];
        if (g) g.setAttribute('transform', `translate(${b.x},${b.y})`);
      });
      return undefined;
    }

    const state = sats.map((b) => ({ ox: b.x, oy: b.y, x: b.x, y: b.y, vx: 0, vy: 0 }));
    let mouse = null;
    let raf = null;
    let running = false;

    const onMove = (e) => {
      const r = svg.getBoundingClientRect();
      mouse = { x: ((e.clientX - r.left) / r.width) * 460, y: ((e.clientY - r.top) / r.height) * 420 };
    };
    const onLeave = () => { mouse = null; };

    const tick = () => {
      state.forEach((s, i) => {
        let ax = (s.ox - s.x) * 0.02;
        let ay = (s.oy - s.y) * 0.02;
        if (mouse) {
          const dx = s.x - mouse.x;
          const dy = s.y - mouse.y;
          const dist = Math.hypot(dx, dy);
          if (dist < 110 && dist > 0) {
            const f = ((110 - dist) / 110) * 1.6;
            ax += (dx / dist) * f;
            ay += (dy / dist) * f;
          }
        }
        s.vx = (s.vx + ax) * 0.9;
        s.vy = (s.vy + ay) * 0.9;
        s.x += s.vx;
        s.y += s.vy;
        const g = groupRefs.current[i];
        if (g) g.setAttribute('transform', `translate(${s.x},${s.y})`);
      });
      raf = requestAnimationFrame(tick);
    };

    const start = () => { if (!running) { running = true; raf = requestAnimationFrame(tick); } };
    const stop = () => { running = false; if (raf) cancelAnimationFrame(raf); raf = null; };

    const core = coreRef.current;
    const onCoreClick = () => {
      state.forEach((s) => { s.vx += (Math.random() - 0.5) * 22; s.vy += (Math.random() - 0.5) * 22; });
      start();
    };

    svg.addEventListener('mousemove', onMove);
    svg.addEventListener('mouseleave', onLeave);
    if (core) core.addEventListener('click', onCoreClick);

    // run the loop ONLY while the section is on-screen
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { setEntered(true); start(); } else { stop(); }
      });
    }, { threshold: 0.05 });
    io.observe(svg);

    return () => {
      stop();
      io.disconnect();
      svg.removeEventListener('mousemove', onMove);
      svg.removeEventListener('mouseleave', onLeave);
      if (core) core.removeEventListener('click', onCoreClick);
    };
  }, [sats]);

  return (
    <section
      className="py-24"
      style={{ backgroundColor: '#f1f1ef' }}
      data-testid="practice-bubbles-section"
    >
      <div className="pr-spine">
        <div className="grid gap-16 items-center" style={{ gridTemplateColumns: '1fr' }}>
          <style>{`
            @media (min-width: 881px){ .thousand-grid{ grid-template-columns: 1fr 1fr !important; } }
            .thou-lbl{ opacity:0; transform:scale(0.6); transition:opacity .6s ease-out, transform .6s ease-out; }
            .thou-lbl.in{ opacity:1; transform:scale(1); }
            .thou-dec{ opacity:0; transition:opacity .5s ease-out; }
            .thou-dec.in{ opacity:1; }
          `}</style>
          <div className="thousand-grid grid gap-16 items-center" style={{ gridTemplateColumns: '1fr' }}>
            {/* left — copy */}
            <div>
              <p
                className="text-xs uppercase mb-6"
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, letterSpacing: '0.24em', color: '#4A6A9C' }}
                data-testid="practice-bubbles-kicker"
              >
                what the thousand are made of
              </p>
              <h2 className="mb-8" data-testid="practice-bubbles-heading">
                <span
                  className="block text-3xl md:text-4xl text-[#002F6C] lowercase"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}
                >
                  one practice.
                </span>
                <span
                  className="block text-3xl md:text-4xl text-[#002F6C] lowercase"
                  style={{ fontFamily: 'Cormorant, Georgia, serif', fontWeight: 500 }}
                >
                  every discipline grows from it.
                </span>
              </h2>
              <p
                className="text-gray-600 lowercase leading-relaxed max-w-md"
                style={{ fontFamily: 'Lato, sans-serif', fontWeight: 300 }}
                data-testid="practice-bubbles-lede"
              >
                the blue is studio 2+2. landscape is the largest ring around it. move your cursor through the practice — it responds. touch the blue and it plays.
              </p>
            </div>

            {/* right — bubble scene */}
            <div className="w-full">
              <svg
                ref={svgRef}
                viewBox="0 0 460 420"
                className="w-full h-auto"
                role="img"
                aria-label="the disciplines of studio 2+2 as bubbles orbiting the practice"
                data-testid="practice-bubbles-svg"
              >
                {/* core — navy studio 2+2 */}
                <g ref={coreRef} style={{ cursor: 'pointer' }} data-testid="practice-bubbles-core">
                  <g className={`thou-dec${entered ? ' in' : ''}`}>
                    <circle cx={CORE.x} cy={CORE.y} r={CORE.r} fill={NAVY} fillOpacity={0.1} stroke={NAVY} strokeOpacity={0.75} strokeWidth={1.5} />
                    <ellipse cx={CORE.x - CORE.r * 0.3} cy={CORE.y - CORE.r * 0.36} rx={CORE.r * 0.34} ry={CORE.r * 0.2} fill="#ffffff" fillOpacity={0.35} />
                    <text
                      x={CORE.x}
                      y={CORE.y}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fill={NAVY}
                      fontSize="17"
                      style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}
                    >
                      studio 2+2
                    </text>
                  </g>
                </g>

                {/* satellites */}
                {sats.map((b, i) => {
                  const [hi, mid, deep] = EA[b.k];
                  const innerCls = `${b.label ? 'thou-lbl' : 'thou-dec'}${entered ? ' in' : ''}`;
                  const innerDelay = b.label ? ENTER_DELAY[b.label] : 40;
                  return (
                    <g
                      key={i}
                      ref={(el) => { groupRefs.current[i] = el; }}
                      transform={`translate(${b.x},${b.y})`}
                      data-testid={b.label ? `practice-bubble-${b.label}` : `practice-bubble-dot-${i}`}
                    >
                      <g className={innerCls} style={{ transitionDelay: `${innerDelay}ms` }}>
                        <circle r={b.r} fill={mid} fillOpacity={0.14} stroke={mid} strokeOpacity={0.8} strokeWidth={1.5} />
                        <circle r={Math.max(1, b.r - 4)} fill="none" stroke={hi} strokeOpacity={0.45} strokeWidth={1} />
                        <ellipse cx={-b.r * 0.3} cy={-b.r * 0.34} rx={b.r * 0.32} ry={b.r * 0.18} fill={hi} fillOpacity={0.5} />
                        {b.label && (
                          <text
                            x={0}
                            y={0}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fill={deep}
                            fontSize={Math.max(8, b.r * 0.26)}
                            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, pointerEvents: 'none' }}
                          >
                            {b.label}
                          </text>
                        )}
                      </g>
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PracticeBubbles;
